import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MessageCircle, CheckCircle2, Share2, Check, PackageSearch, Mail } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import FloatingWhatsApp from "./FloatingWhatsApp";
import useSeo from "../hooks/useSeo";
import { wa, logClick } from "../mock";
import { ContactLink } from "./ContactLink";
import { ProductPhotos } from "./ProductPhotos";
import AddToListButton from "./AddToListButton";
import { Minus, Plus } from "lucide-react";
import { specificCondition, isBrandNew } from "../lib/condition";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (n) => "\u20b9" + Number(n || 0).toLocaleString("en-IN");
const SERVICE_FOR = {
  Laptop: ["/laptops-workstations", "Sell used laptops"],
  Desktop: ["/desktops", "Used desktops"],
  Server: ["/servers-storage", "Servers & storage"],
  Storage: ["/servers-storage", "Servers & storage"],
  Networking: ["/networking-equipment", "Networking equipment"],
  UPS: ["/ups-power", "UPS & power"],
  Printer: ["/used-printers-ups", "Printers & UPS"],
  Monitor: ["/office-equipment", "Office equipment"],
  TV: ["/office-equipment", "Office equipment"],
  Furniture: ["/office-equipment", "Office equipment"],
};

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/products/${id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setP(d))
      .catch(() => setP(null))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [id]);

  const primaryImg = p ? ((p.images && p.images.length ? p.images[0] : p.img) || "") : "";
  const enquiryMsg = p
    ? `Hi Kapatia, I'm interested in ${p.code ? p.code + ": " : ""}${p.name} (${p.brand}).\nProduct: ${window.location.href}\nRequired quantity: ${qty}${p.excludes ? `\nNote: ${p.excludes}` : ""}`
    : "";

  useSeo({
    title: p ? `${p.name} | Kapatia Pvt Ltd` : "Product | Kapatia Pvt Ltd",
    description: p
      ? (isBrandNew(p)
          ? `${p.name} (${p.brand}) — brand new ${p.category} from Kapatia Pvt Ltd. ${p.price_on_request ? "Price on request" : inr(p.price)}. Quick WhatsApp enquiry.`
          : `${p.name} (${p.brand}) — pre-owned ${p.category} in working condition from Kapatia Pvt Ltd. ${p.price_on_request ? "Price on request" : inr(p.price)}. Quick WhatsApp enquiry.`)
      : "Pre-owned IT & office equipment from Kapatia Pvt Ltd.",
    keywords: p ? [p.brand, p.name, p.category, p.subcategory, p.code, ...(isBrandNew(p) ? ["brand new", "new"] : ["refurbished", "used", "second hand"]), "Kapatia"].filter(Boolean).join(", ") : undefined,
    image: primaryImg || undefined,
    imageAlt: p ? p.name : undefined,
  });

  // Product structured data (schema.org) for rich results on product pages.
  useEffect(() => {
    if (!p) return;
    const url = "https://www.kapatia.com/product/" + p.id;
    const inStock = !p.sold && (p.qty == null || p.qty > 0);
    const availability = inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock";
    const itemCondition = isBrandNew(p) ? "https://schema.org/NewCondition" : "https://schema.org/UsedCondition";
    const ld = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: p.name,
      sku: p.code || undefined,
      category: p.category || undefined,
      brand: p.brand ? { "@type": "Brand", name: p.brand } : undefined,
      image: primaryImg ? new URL(primaryImg, window.location.origin).href : undefined,
      itemCondition,
      url,
      offers: p.price_on_request ? undefined : {
        "@type": "Offer",
        priceCurrency: "INR",
        price: p.price,
        availability,
        itemCondition,
        url,
        seller: { "@type": "Organization", name: "Kapatia Pvt Ltd" },
      },
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-ld", "product");
    el.text = JSON.stringify(ld);
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, [p, primaryImg]);

  const share = async () => {
    const url = window.location.href;
    const text = p ? `${p.name} — Kapatia Pvt Ltd` : "Kapatia Pvt Ltd";
    try {
      if (navigator.share) { await navigator.share({ title: text, text, url }); return; }
    } catch (err) { if (err && err.name === "AbortError") return; }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.debug("Share/clipboard copy failed", err);
    }
  };

  return (
    <div className="public-site" style={{ background: "var(--cream)" }}>
      <Header />
      <main className="container py-10" data-testid="product-detail-page" style={{ minHeight: "60vh" }}>
        <Link to="/#stock" className="inline-flex items-center gap-1.5 font-heading text-[13.5px] font-bold" style={{ color: "var(--green-800)" }}>
          <ArrowLeft size={15} /> Back to stock
        </Link>

        {loading ? (
          <p className="mt-10 text-center" style={{ color: "var(--muted)" }}>Loading product…</p>
        ) : !p ? (
          <div className="mt-10 grid place-items-center gap-3 py-16 text-center" data-testid="product-not-found">
            <PackageSearch size={40} style={{ color: "var(--muted)" }} />
            <p className="font-heading font-bold" style={{ color: "var(--green-950)" }}>This product is no longer available</p>
            <Link to="/#stock" className="font-heading font-bold" style={{ color: "var(--orange-700)" }}>Browse current stock →</Link>
          </div>
        ) : (
          <div className="mt-6 grid gap-8 lg:grid-cols-2">
            <div className="group relative overflow-hidden rounded-2xl border bg-white" style={{ borderColor: "var(--line)", boxShadow: "0 12px 30px rgba(45,106,79,0.08)" }}>
              <ProductPhotos p={p} heightCls="h-[320px] sm:h-[420px]" />
              {p.sold && (
                <span className="absolute right-4 top-4 rounded-full px-3 py-1 font-heading text-[12px] font-extrabold text-white" style={{ background: "var(--orange-700)" }}>SOLD</span>
              )}
            </div>
            <div>
              <span className="text-[12px] font-bold uppercase tracking-wide" style={{ color: "var(--orange-700)" }}>{p.brand}</span>
              <h1 className="mt-1 font-heading text-[26px] font-bold leading-snug sm:text-[32px]" style={{ color: "var(--ink)" }} data-testid="product-detail-name">{p.name}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-2.5">
                <Link to={`/stock/${encodeURIComponent(p.category)}`} className="rounded-full border bg-white px-3.5 py-1.5 font-heading text-[12.5px] font-bold" style={{ borderColor: "var(--line)", color: "var(--green-900)" }}>{p.category}</Link>
                {p.code && <span data-testid="product-detail-code" className="rounded-full px-3.5 py-1.5 font-heading text-[12.5px] font-bold" style={{ background: "var(--green-100)", color: "var(--green-900)" }}>{p.code}</span>}
                {!p.sold && (p.qty > 1
                  ? <span className="rounded-full px-3.5 py-1.5 font-heading text-[12.5px] font-bold" style={{ background: "var(--yellow-500)", color: "var(--green-950)" }}>{p.qty} in stock</span>
                  : <span className="rounded-full px-3.5 py-1.5 font-heading text-[12.5px] font-bold" style={{ background: "var(--green-100)", color: "var(--green-900)" }}>1 in stock</span>)}
              </div>
              <span className="mt-4 inline-flex items-center gap-1.5 text-[13.5px] font-bold" style={{ color: "var(--bright-green-strong)" }}>
                <CheckCircle2 size={15} /> {specificCondition(p) || "Good working condition"}
              </span>
              {!isBrandNew(p) && (
                <p className="mt-1 text-[12.5px]" style={{ color: "var(--muted)" }}>Pre-owned / open-box company asset</p>
              )}
              {p.excludes && (
                <p data-testid="product-detail-excludes" className="mt-2 inline-block rounded-md px-2.5 py-1 text-[12.5px] font-bold" style={{ background: "#fff4e5", color: "var(--orange-700)" }}>
                  {p.excludes}
                </p>
              )}
              {p.review_status === "Details to confirm" && (
                <p data-testid="product-detail-review" className="mt-2 text-[12px]" style={{ color: "var(--muted)" }}>
                  Some details are still being confirmed — please verify exact configuration when you enquire.
                </p>
              )}
              <div className="mt-5">
                <div className="text-[11.5px] font-bold uppercase tracking-wide" style={{ color: "var(--muted)" }}>
                  Price{p.price_unit && p.price_unit !== "per item" ? ` · ${p.price_unit}` : ""}
                </div>
                {p.price_on_request
                  ? <span className="font-heading text-[22px] font-extrabold" style={{ color: "var(--green-800)" }}>Price on request</span>
                  : <span className="font-heading text-[30px] font-extrabold" style={{ color: "var(--orange-700)" }}>{inr(p.price)}</span>}
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <div className="inline-flex items-center gap-2">
                  <span className="text-[12.5px] font-bold uppercase tracking-wide" style={{ color: "var(--muted)" }}>Qty</span>
                  <div className="inline-flex items-center rounded-full border" style={{ borderColor: "var(--line)" }}>
                    <button onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Decrease quantity" data-testid="product-qty-dec" className="grid h-10 w-10 place-items-center" style={{ color: "var(--green-900)" }}><Minus size={16} /></button>
                    <span className="min-w-8 text-center font-heading text-[15px] font-bold" data-testid="product-qty">{qty}</span>
                    <button onClick={() => setQty((q) => q + 1)} aria-label="Increase quantity" data-testid="product-qty-inc" className="grid h-10 w-10 place-items-center" style={{ color: "var(--green-900)" }}><Plus size={16} /></button>
                  </div>
                </div>
                <AddToListButton product={p} qty={qty} variant="detail" />
              </div>
              <div className="mt-3 flex flex-wrap gap-3">
                <a href={wa(enquiryMsg)}
                   onClick={() => logClick(p, "product-page")}
                   data-product-code={p.code} data-product-category={p.category}
                   target="_blank" rel="noopener noreferrer" data-testid="product-detail-whatsapp"
                   className="kp-button inline-flex items-center gap-2 rounded-full px-6 py-3 font-heading text-[14.5px] font-bold text-white transition-transform hover:-translate-y-0.5"
                   style={{ background: "linear-gradient(135deg, var(--bright-green), var(--bright-green-strong))" }}>
                  <MessageCircle size={16} /> Enquire on WhatsApp
                </a>
                <a href={`mailto:info@kapatia.com?subject=${encodeURIComponent("Stock enquiry - " + (p.code || p.name))}&body=${encodeURIComponent(enquiryMsg)}`}
                   onClick={() => logClick(p, "product-page-email")}
                   data-product-code={p.code} data-product-category={p.category}
                   data-testid="product-detail-email"
                   className="kp-button inline-flex items-center gap-2 rounded-full border bg-white px-6 py-3 font-heading text-[14.5px] font-bold transition-transform hover:-translate-y-0.5"
                   style={{ borderColor: "var(--line)", color: "var(--green-900)" }}>
                  <Mail size={16} /> Email enquiry
                </a>
                <button onClick={share} data-testid="product-detail-share"
                        className="kp-button inline-flex items-center gap-2 rounded-full border bg-white px-6 py-3 font-heading text-[14.5px] font-bold transition-transform hover:-translate-y-0.5"
                        style={{ borderColor: "var(--line)", color: "var(--green-900)" }}>
                  {copied ? <Check size={16} style={{ color: "var(--green-700)" }} /> : <Share2 size={16} />} {copied ? "Link copied" : "Share"}
                </button>
              </div>
              <ContactLink testId="product-detail-phone" className="mt-5" />
              <div data-testid="product-detail-warranty" className="mt-6 rounded-xl border p-4" style={{ borderColor: "var(--line)", background: "#fff" }}>
                <div className="font-heading text-[13px] font-bold" style={{ color: "var(--green-900)" }}>Warranty</div>
                {p.warranty_display && (
                  <p className="mt-1 text-[13px] font-semibold" style={{ color: "var(--ink)" }}>This item: {p.warranty_display}</p>
                )}
                <p className="mt-1 text-[12.5px]" style={{ color: "var(--muted)" }}>1 month testing warranty unless otherwise stated.</p>
              </div>
              <nav aria-label="Related pages" data-testid="product-related-links" className="mt-6 flex flex-wrap gap-2">
                <Link to={`/stock/${encodeURIComponent(p.category)}`}
                      className="inline-flex rounded-full border bg-white px-4 py-2 font-heading text-[12.5px] font-bold"
                      style={{ borderColor: "var(--line)", color: "var(--green-900)" }}>More {p.category}</Link>
                <Link to="/store"
                      className="inline-flex rounded-full border bg-white px-4 py-2 font-heading text-[12.5px] font-bold"
                      style={{ borderColor: "var(--line)", color: "var(--green-900)" }}>Browse store</Link>
                {SERVICE_FOR[p.category] && (
                  <Link to={SERVICE_FOR[p.category][0]}
                        className="inline-flex rounded-full border bg-white px-4 py-2 font-heading text-[12.5px] font-bold"
                        style={{ borderColor: "var(--line)", color: "var(--green-900)" }}>{SERVICE_FOR[p.category][1]}</Link>
                )}
              </nav>
            </div>
          </div>
        )}
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
